import React, { useState, useEffect, useRef } from 'react';
import './JobPostingSelector.css';

/**
 * JobPostingSelector Component
 *
 * Searchable dropdown for selecting job postings with:
 * - Real-time search (debounced)
 * - Recent/suggested job postings
 * - Create new job posting (slide-in panel)
 * - Keyboard navigation
 */
const JobPostingSelector = ({ value, onChange, onSelectJobPosting, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [jobPostings, setJobPostings] = useState([]);
  const [suggestedPostings, setSuggestedPostings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPosting, setSelectedPosting] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    title: '',
    location: '',
    workMode: 'remote',
    employmentType: 'full-time',
    experienceLevel: 'mid',
    description: ''
  });
  const [createError, setCreateError] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  const searchTimeoutRef = useRef(null);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load suggested postings on mount
  useEffect(() => {
    loadSuggestedPostings();
  }, []);

  // Load selected posting details if value changes
  useEffect(() => {
    if (value && !selectedPosting) {
      loadPostingById(value);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  useEffect(() => {
    if (searchQuery.length >= 2) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        searchJobPostings(searchQuery);
      }, 300);

      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    } else if (searchQuery.length === 0) {
      setJobPostings([]);
    }
  }, [searchQuery]);

  const loadSuggestedPostings = async () => {
    try {
      const response = await fetch('/api/jobs?status=active&limit=5', {
        headers: {
          'x-session-id': localStorage.getItem('sessionId')
        }
      });
      const data = await response.json();
      setSuggestedPostings(data.jobs || []);
    } catch (err) {
      console.error('Failed to load suggested job postings:', err);
    }
  };

  const loadPostingById = async (id) => {
    try {
      const response = await fetch(`/api/jobs/${id}`, {
        headers: {
          'x-session-id': localStorage.getItem('sessionId')
        }
      });
      const data = await response.json();
      if (data.success) {
        setSelectedPosting(data.job);
      }
    } catch (err) {
      console.error('Failed to load job posting:', err);
    }
  };

  const searchJobPostings = async (query) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/jobs?search=${encodeURIComponent(query)}&status=active&limit=20`, {
        headers: {
          'x-session-id': localStorage.getItem('sessionId')
        }
      });
      const data = await response.json();
      setJobPostings(data.jobs || []);
    } catch (err) {
      console.error('Search failed:', err);
      setJobPostings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (posting) => {
    setSelectedPosting(posting);
    onChange(posting._id);
    if (onSelectJobPosting) {
      onSelectJobPosting(posting);
    }
    setIsOpen(false);
    setSearchQuery('');
    setJobPostings([]);
  };

  const handleInputClick = () => {
    setIsOpen(true);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const handleKeyDown = (e) => {
    const items = searchQuery ? jobPostings : suggestedPostings;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => (prev < items.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && focusedIndex >= 0) {
      e.preventDefault();
      handleSelect(items[focusedIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleCreatePosting = async (e) => {
    e.preventDefault();
    setCreateError('');

    if (!createFormData.title || !createFormData.location || !createFormData.description) {
      setCreateError('Title, Location, and Description are required');
      return;
    }

    try {
      setCreateLoading(true);
      const response = await fetch('/api/jobs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-id': localStorage.getItem('sessionId')
        },
        body: JSON.stringify({
          ...createFormData,
          company: 'SecureLayer7', // Default company
          status: 'active' // Make job immediately visible
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create job posting');
      }

      const newPosting = data.job;
      handleSelect(newPosting);
      setShowCreatePanel(false);
      setCreateFormData({
        title: '',
        location: '',
        workMode: 'remote',
        employmentType: 'full-time',
        experienceLevel: 'mid',
        description: ''
      });
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreateLoading(false);
    }
  };

  const displayValue = selectedPosting // eslint-disable-line no-unused-vars
    ? `${selectedPosting.title} - ${selectedPosting.location}`
    : 'Select a job posting...';

  const itemsToShow = searchQuery ? jobPostings : suggestedPostings;

  return (
    <div className="job-posting-selector" ref={wrapperRef}>
      <div
        className={`selector-display ${error ? 'has-error' : ''} ${isOpen ? 'is-open' : ''}`}
        onClick={handleInputClick}
      >
        <div className="selected-value">
          {selectedPosting ? (
            <div className="selected-posting">
              <div className="posting-title">{selectedPosting.title}</div>
              <div className="posting-meta">{selectedPosting.location} • {selectedPosting.workMode}</div>
            </div>
          ) : (
            <span className="placeholder">Select job posting...</span>
          )}
        </div>
        <svg className="dropdown-icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </div>

      {error && <span className="error-message">{error}</span>}

      {isOpen && (
        <div className="posting-dropdown">
          <div className="search-box">
            <svg className="search-icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              placeholder="Search job postings by title or location..."
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              className="search-input"
            />
          </div>

          {!searchQuery && suggestedPostings.length > 0 && (
            <div className="section-label">Active Job Postings</div>
          )}

          {loading && (
            <div className="loading-state">Searching...</div>
          )}

          {!loading && itemsToShow.length === 0 && searchQuery.length >= 2 && (
            <div className="empty-state">
              <p>No job postings found</p>
              <button
                className="create-new-button"
                onClick={() => setShowCreatePanel(true)}
              >
                + Create New Job Posting
              </button>
            </div>
          )}

          {!loading && itemsToShow.length === 0 && searchQuery.length < 2 && !searchQuery && suggestedPostings.length === 0 && (
            <div className="empty-state">
              <p>No active job postings</p>
              <button
                className="create-new-button"
                onClick={() => setShowCreatePanel(true)}
              >
                + Create New Job Posting
              </button>
            </div>
          )}

          {!loading && itemsToShow.length > 0 && (
            <div className="postings-list">
              {itemsToShow.map((posting, index) => (
                <div
                  key={posting._id}
                  className={`posting-option ${focusedIndex === index ? 'focused' : ''}`}
                  onClick={() => handleSelect(posting)}
                  onMouseEnter={() => setFocusedIndex(index)}
                >
                  <div className="posting-info">
                    <div className="posting-title">{posting.title}</div>
                    <div className="posting-meta">
                      {posting.location} • {posting.workMode} • {posting.experienceLevel}
                    </div>
                  </div>
                  <span className="posting-status">
                    {posting.applicationCount || 0} applications
                  </span>
                </div>
              ))}
            </div>
          )}

          {itemsToShow.length > 0 && (
            <div className="dropdown-footer">
              <button
                className="create-footer-button"
                onClick={() => setShowCreatePanel(true)}
              >
                + Create New Job Posting
              </button>
            </div>
          )}
        </div>
      )}

      {/* Create Job Posting Panel */}
      {showCreatePanel && (
        <>
          <div className="panel-overlay" onClick={() => setShowCreatePanel(false)} />
          <div className="create-posting-panel">
            <div className="panel-header">
              <h2>Create Job Posting</h2>
              <button className="close-button" onClick={() => setShowCreatePanel(false)}>×</button>
            </div>

            <form onSubmit={handleCreatePosting} className="create-form">
              {createError && <div className="form-error">{createError}</div>}

              <div className="form-group">
                <label>Job Title *</label>
                <input
                  type="text"
                  value={createFormData.title}
                  onChange={(e) => setCreateFormData({ ...createFormData, title: e.target.value })}
                  placeholder="e.g., Security Consultant"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Location *</label>
                  <input
                    type="text"
                    value={createFormData.location}
                    onChange={(e) => setCreateFormData({ ...createFormData, location: e.target.value })}
                    placeholder="e.g., Remote, Mumbai"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Work Mode</label>
                  <select
                    value={createFormData.workMode}
                    onChange={(e) => setCreateFormData({ ...createFormData, workMode: e.target.value })}
                  >
                    <option value="remote">Remote</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="onsite">Onsite</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Employment Type</label>
                  <select
                    value={createFormData.employmentType}
                    onChange={(e) => setCreateFormData({ ...createFormData, employmentType: e.target.value })}
                  >
                    <option value="full-time">Full-time</option>
                    <option value="part-time">Part-time</option>
                    <option value="contract">Contract</option>
                    <option value="internship">Internship</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Experience Level</label>
                  <select
                    value={createFormData.experienceLevel}
                    onChange={(e) => setCreateFormData({ ...createFormData, experienceLevel: e.target.value })}
                  >
                    <option value="entry">Entry Level</option>
                    <option value="junior">Junior</option>
                    <option value="mid">Mid Level</option>
                    <option value="senior">Senior</option>
                    <option value="lead">Lead</option>
                    <option value="principal">Principal</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={createFormData.description}
                  onChange={(e) => setCreateFormData({ ...createFormData, description: e.target.value })}
                  placeholder="Brief job description..."
                  rows="4"
                  required
                />
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-button" onClick={() => setShowCreatePanel(false)}>
                  Cancel
                </button>
                <button type="submit" className="submit-button" disabled={createLoading}>
                  {createLoading ? 'Creating...' : 'Create & Select'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default JobPostingSelector;
